/**
 * LiveChatWidget.jsx
 *
 * Chat bubble component for all pages with:
 * - Auto-greeting based on business hours
 * - AI-first conversation (first 2-3 messages)
 * - Real-time sync with support dashboard
 *
 * Restored Nov 3 2026 (owner): the in-site chat is back for every visitor; the
 * Havlo launcher stays hidden. New chats and visitor messages alert the Ops
 * admins (notifyOpsOnLiveChat). Any page can open it with
 * window.dispatchEvent(new Event("scope180:open-chat")).
 */
import React, { useState, useEffect, useRef } from "react";
import { db, getFunctionsClient } from "../../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  addDoc,
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Bot, Bug, User } from "lucide-react";
import TesterFeedbackWidget from "../tester/TesterFeedbackWidget";
import ChatText from "./ChatText";

// Business hours: 8am-8pm Eastern (handles daylight saving)
function isBusinessHours() {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", { hour: "numeric", hourCycle: "h23", timeZone: "America/New_York" }).format(new Date())
  );
  return hour >= 8 && hour < 20;
}

// A visitor asking for a person, in any of the ways people ask (owner, Nov 3 2026)
const WANTS_HUMAN_RE =
  /\b(human(?! (resource|centered|centred|factor))|real person|live (rep|agent|person|support|chat)|representative|someone (real|there|here|available)|anyone (there|here|available)|is (someone|anyone|anybody|she|he|they|somebody) (there|here|around|available|on)|are you (there|real|a (bot|person|human))|(talk|speak|chat) (to|with) (someone|somebody|a person|an agent|a rep|a human|staff|the team|her|him|the owner|support)|help me now|need help now|customer (service|support))\b/i;

// The one professional line used whenever a person is needed
const handoffMessage = () =>
  isBusinessHours()
    ? "I'm contacting a member of our team now to assist you. Please stay here, someone will join this chat shortly."
    : "I'm contacting a member of our team now. We're outside business hours (8am-8pm Eastern), so a team member will reply right here, and by email too if you leave your email address in this chat.";

// Same id useVisitorPresence uses, so the Support Queue can match a chat to the live visitor
function presenceVisitorId() {
  try { return localStorage.getItem("s180_visitor_id") || null; } catch { return null; }
}

function generateVisitorId() {
  const stored = localStorage.getItem("scope180_visitor_id");
  if (stored) return stored;
  const id = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem("scope180_visitor_id", id);
  return id;
}

// How many messages of a chat this browser has already seen, so the bubble's
// unread badge survives page loads (otherwise every past reply counts as new).
const readKey = (chatId) => `scope180_chat_read_${chatId}`;
function readMark(chatId) {
  try { return Number(localStorage.getItem(readKey(chatId))) || 0; } catch { return 0; }
}
function saveMark(chatId, count) {
  try { if (chatId && count) localStorage.setItem(readKey(chatId), String(count)); } catch { /* private window */ }
}
const unreadAfter = (msgs, seen) => msgs.slice(seen).filter((m) => m.sender !== "visitor").length;

export default function LiveChatWidget() {
  const { currentUser, client } = useAuth();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [visitorName, setVisitorName] = useState("");
  const [showNamePrompt, setShowNamePrompt] = useState(!client?.name);
  const [isHumanTakeover, setIsHumanTakeover] = useState(false);
  const [repName, setRepName] = useState(null);
  const [isOnline, setIsOnline] = useState(isBusinessHours());
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const messagesEndRef = useRef(null);
  const visitorId = useRef(generateVisitorId());
  const lastReadCountRef = useRef(0);
  const bgChatRef = useRef({ id: null, count: 0 }); // latest active chat seen while minimized

  // Auto-scroll to bottom and mark as read when chat is open
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    if (isOpen && messages.length > 0) {
      lastReadCountRef.current = messages.length;
      saveMark(conversationId, messages.length);
      setUnreadCount(0);
    }
  }, [messages, isOpen, conversationId]);

  // When chat opens, mark all as read
  useEffect(() => {
    if (isOpen) {
      lastReadCountRef.current = messages.length;
      saveMark(bgChatRef.current.id, bgChatRef.current.count);
      // Resume the visitor's open chat instead of showing an empty window
      if (!conversationId && bgChatRef.current.id) setConversationId(bgChatRef.current.id);
      setUnreadCount(0);
    }
  }, [isOpen, messages.length, conversationId]);

  // Other parts of the site (e.g. the AI Assistant's "Talk to our team") open the chat
  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener("scope180:open-chat", open);
    return () => window.removeEventListener("scope180:open-chat", open);
  }, []);

  // Check business hours every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setIsOnline(isBusinessHours());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Background listener: detect unread messages even when chat is minimized
  useEffect(() => {
    if (!currentUser?.uid) return;
    // Only listen when chat is NOT open and no active conversationId
    // (when conversationId exists, the main subscription handles it)
    if (conversationId) return;

    const bgQuery = query(
      collection(db, "liveChats"),
      where("userId", "==", currentUser.uid),
      where("status", "==", "active"),
      orderBy("lastMessageAt", "desc"),
      limit(1)
    );

    const unsubscribe = onSnapshot(bgQuery, (snap) => {
      if (snap.empty) {
        setUnreadCount(0);
        return;
      }
      const chatId = snap.docs[0].id;
      const allMessages = snap.docs[0].data().messages || [];
      bgChatRef.current = { id: chatId, count: allMessages.length };
      // Only replies that arrived after this browser last saw the chat count as unread
      if (isOpen) saveMark(chatId, allMessages.length);
      else setUnreadCount(unreadAfter(allMessages, readMark(chatId)));
    }, () => {
      // Ignore errors (index may not exist)
    });

    return () => unsubscribe();
  }, [currentUser?.uid, conversationId, isOpen]);

  // Visitor presence is tracked once, by useVisitorPresence (which skips the owner and staff).


  // Subscribe to conversation updates
  useEffect(() => {
    if (!conversationId) return;

    let isCurrent = true;
    let unsubscribe = null;

    try {
      unsubscribe = onSnapshot(
        doc(db, "liveChats", conversationId),
        (docSnap) => {
          if (!isCurrent) return;
          if (docSnap.exists()) {
            const data = docSnap.data();
            const allMsgs = data.messages || [];
            setMessages(allMsgs);
            setIsHumanTakeover(data.humanTakeover || false);
            setRepName(data.humanTakeover ? data.assignedName || null : null);

            // Track unread when chat is minimized
            if (!isOpen) {
              setUnreadCount(unreadAfter(allMsgs, Math.max(lastReadCountRef.current, readMark(conversationId))));
            }
          }
        },
        (err) => {
          if (!isCurrent) return;
          console.error("Chat subscription error:", err);
        }
      );
    } catch (err) {
      console.error("Failed to setup chat subscription:", err);
    }

    return () => {
      isCurrent = false;
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (e) {
          // Ignore
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Auto-start conversation for authenticated dashboard users
  useEffect(() => {
    if (isOpen && client?.name && !conversationId) {
      startConversation(client.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, client?.name]);

  // Start conversation — reuse existing active chat if one exists
  const startConversation = async (name) => {
    setVisitorName(name);
    setShowNamePrompt(false);

    try {
      // Check for existing active chat for this visitor/user
      const lookupField = currentUser?.uid ? "userId" : "visitorId";
      const lookupValue = currentUser?.uid || visitorId.current;

      const existingQuery = query(
        collection(db, "liveChats"),
        where(lookupField, "==", lookupValue),
        where("status", "==", "active"),
        orderBy("lastMessageAt", "desc"),
        limit(1)
      );

      const existingSnap = await getDocs(existingQuery);

      if (!existingSnap.empty) {
        // Reactivate existing chat thread
        const existingDoc = existingSnap.docs[0];
        setConversationId(existingDoc.id);
        const data = existingDoc.data();
        setMessages(data.messages || []);
        setIsHumanTakeover(data.humanTakeover || false);
        setRepName(data.humanTakeover ? data.assignedName || null : null);

        // Update current page without adding a return message
        await updateDoc(doc(db, "liveChats", existingDoc.id), {
          lastMessageAt: serverTimestamp(),
          currentPage: location.pathname,
          navigationHistory: arrayUnion(location.pathname),
          presenceVisitorId: presenceVisitorId(),
        });
        return;
      }

      // No existing chat — create a new one
      const greeting = isOnline
        ? "Thank you for visiting TheScope180! If you need assistance, our Operations team is online. How can I help you today?\n\nIf something is broken, you can send feedback using the bug icon above and include a screenshot as well."
        : "Thank you for visiting TheScope180! Our team is available 8am-8pm Eastern. I can answer most questions right now, and if you need a person, a team member will reply here (and by email if you leave one).\n\nIf something is broken, you can send feedback using the bug icon above and include a screenshot as well.";

      const docRef = await addDoc(collection(db, "liveChats"), {
        visitorId: visitorId.current,
        presenceVisitorId: presenceVisitorId(),
        visitorName: name,
        userId: currentUser?.uid || null,
        userEmail: currentUser?.email || null,
        status: "active",
        humanTakeover: false,
        aiMessageCount: 1,
        assignedTo: null,
        assignedName: null,
        messages: [
          {
            id: `ai-${Date.now()}`,
            text: greeting,
            sender: "ai",
            senderName: "Support AI",
            timestamp: new Date().toISOString(),
          },
        ],
        createdAt: serverTimestamp(), // required by the liveChats create rule
        startedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        currentPage: location.pathname,
        navigationHistory: [location.pathname],
      });

      setConversationId(docRef.id);
    } catch (error) {
      console.error("Failed to start conversation:", error);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!inputText.trim() || sending || !conversationId) return;

    const messageText = inputText.trim();
    setInputText("");
    setSending(true);

    try {
      const newMessage = {
        id: `visitor-${Date.now()}`,
        text: messageText,
        sender: "visitor",
        senderName: visitorName,
        timestamp: new Date().toISOString(),
      };

      // Add message to Firestore
      await updateDoc(doc(db, "liveChats", conversationId), {
        messages: arrayUnion(newMessage),
        lastMessageAt: serverTimestamp(),
        currentPage: location.pathname,
        navigationHistory: arrayUnion(location.pathname),
      });

      // AI responds until human takes over or user explicitly asks for live rep
      if (!isHumanTakeover) {
        // Check if user is asking for a human
        const wantsHuman = WANTS_HUMAN_RE.test(messageText);

        if (wantsHuman) {
          // User wants a human - tell them once, professionally, and flag the chat
          // (notifyOpsOnLiveChat alerts the Ops team: bell, email and push)
          await updateDoc(doc(db, "liveChats", conversationId), {
            messages: arrayUnion({
              id: `ai-${Date.now()}`,
              text: handoffMessage(),
              sender: "ai",
              senderName: "Support AI",
              timestamp: new Date().toISOString(),
            }),
            needsHumanAttention: true,
            humanRequestedAt: serverTimestamp(),
            lastMessageAt: serverTimestamp(),
          });
        } else {
          // Continue AI conversation. If the call itself fails, hand off to a
          // person instead of leaving the visitor waiting.
          try {
            const chatSupportAI = httpsCallable(getFunctionsClient(), "chatSupportAI");
            await chatSupportAI({
              action: "chat",
              messages: [...messages, newMessage],
              visitorName,
              conversationId,
            });
          } catch (aiErr) {
            console.error("Chat AI unavailable:", aiErr);
            await updateDoc(doc(db, "liveChats", conversationId), {
              messages: arrayUnion({
                id: `ai-${Date.now()}`,
                text: handoffMessage(),
                sender: "ai",
                senderName: "Support AI",
                timestamp: new Date().toISOString(),
              }),
              needsHumanAttention: true,
              humanRequestedAt: serverTimestamp(),
              lastMessageAt: serverTimestamp(),
            });
          }
        }
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Minimized chat bubble
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-4 md:right-6 z-50 w-12 h-12 md:w-auto md:h-auto md:p-4 flex items-center justify-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 group"
        aria-label="Open chat"
      >
        <MessageCircle className="w-5 h-5 md:w-6 md:h-6 group-hover:scale-110 transition-transform" />
        {unreadCount > 0 ? (
          <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] bg-red-500 rounded-full border-2 border-white flex items-center justify-center text-[11px] font-bold text-white animate-bounce">
            {unreadCount}
          </span>
        ) : (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse" />
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:bottom-6 md:right-6 z-50 md:w-[380px] md:max-w-[calc(100vw-3rem)] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col max-h-[80vh] md:max-h-[600px]">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            {isHumanTakeover ? <User size={20} className="text-white" /> : <Bot size={20} className="text-white" />}
          </div>
          <div>
            <h3 className="text-white font-bold text-sm">
              {isHumanTakeover && repName ? repName : "TheScope180 Support"}
            </h3>
            <p className="text-white/70 text-xs">
              {isHumanTakeover ? (
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full" />
                  TheScope180 Support team
                </span>
              ) : isOnline ? (
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full" />
                  Online now
                </span>
              ) : (
                "We'll reply soon"
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setFeedbackOpen(true)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            title="Send feedback or report a bug"
          >
            <Bug size={16} className="text-white/80" />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Name Prompt or Chat */}
      {showNamePrompt ? (
        <div className="p-6 flex flex-col items-center justify-center flex-1">
          <Bot size={48} className="text-blue-500 mb-4" />
          <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">
            Welcome to TheScope180!
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
            {isOnline
              ? "Our Operations team is ready to help."
              : "Ask us anything. We'll reply soon, here and by email if you leave one."}
          </p>
          <input
            type="text"
            placeholder="Enter your name to start..."
            value={visitorName}
            onChange={(e) => setVisitorName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && visitorName.trim() && startConversation(visitorName)}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-transparent text-gray-900 dark:text-white mb-3 outline-none focus:border-blue-500"
          />
          <button
            onClick={() => visitorName.trim() && startConversation(visitorName)}
            disabled={!visitorName.trim()}
            className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            Start Chat
          </button>
        </div>
      ) : (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-slate-800/50 min-h-[300px]">
            {messages.map((msg) => msg.sender === "system" ? (
              <p key={msg.id} className="text-center text-xs italic text-gray-500 dark:text-gray-400 px-2">
                {msg.text}
              </p>
            ) : (
              <div
                key={msg.id}
                className={`flex ${msg.sender === "visitor" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                    msg.sender === "visitor"
                      ? "bg-blue-600 text-white rounded-br-sm"
                      : msg.sender === "ai"
                      ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 rounded-bl-sm"
                      : "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-900 dark:text-indigo-100 border border-indigo-200 dark:border-indigo-700 rounded-bl-sm"
                  }`}
                >
                  {msg.sender !== "visitor" && (
                    <p className="text-[10px] font-bold mb-1 opacity-60">
                      {msg.sender === "rep" ? msg.senderName || "TheScope180 Support" : msg.senderName || (msg.sender === "ai" ? "Support AI" : "Support")}
                    </p>
                  )}
                  <div className="text-sm leading-relaxed"><ChatText text={msg.text} /></div>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-slate-700 px-4 py-3 rounded-2xl rounded-bl-sm border border-gray-200 dark:border-gray-600">
                  <Loader2 size={16} className="animate-spin text-blue-500" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Type your message..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-transparent text-gray-900 dark:text-white text-sm outline-none focus:border-blue-500"
              />
              <button
                onClick={sendMessage}
                disabled={!inputText.trim() || sending}
                className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Send size={18} />
              </button>
            </div>
            <p className="text-[10px] text-center text-gray-400 mt-2">
              {isHumanTakeover ? "You're chatting with our support team" : "Powered by TheScope180 Support AI"}
            </p>
          </div>
        </>
      )}

      {/* Feedback widget triggered from chat bug icon */}
      <TesterFeedbackWidget
        isOpen={feedbackOpen}
        onToggle={setFeedbackOpen}
        allowNonTester
      />
    </div>
  );
}
